import React, { useEffect } from "react";

import { CloseOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";

import StyledScrollbar from "../../../UIComponents/StyledScrollbar";
import { getTemplateAccentStyle, isDuplicateTemplateNameMessage } from "../../helper";
import useTemplateAsyncAction from "../../hooks/useTemplateAsyncAction";
import { resolveTemplateAssetUrl } from "../../coaPreviewHelper";
import { getOppositeTemplateSide, TEMPLATE_SIDES, useTemplateContext } from "../../TemplateContext";
import TemplateSectionLoading from "../Common/TemplateSectionLoading";

import { addTemplate, getTemplates } from "../../../../../actions/productTemplate";

import { ReactComponent as ImportTemplateIcon } from "../../../../../assets/images/environment/ImportTemplateIconBlack.svg";
import { ReactComponent as RectangleLine } from "../../../../../assets/images/sample-submission/RectangleLine.svg";

import "./ImportTemplatePanel.css";

const buildImportPayload = async (template, side, signal) => {
  const [logo_image_url, signature_image_url] = await Promise.all([
    resolveTemplateAssetUrl({
      sourceUrl: template.logo_image_url,
      type: "logo_image",
      side,
      signal,
    }),
    resolveTemplateAssetUrl({
      sourceUrl: template.signature_image_url,
      type: "signature_image",
      side,
      signal,
    }),
  ]);

  return {
    name: template.name,
    tags: template.tags,
    logo_image_url,
    company_name: template.company_name || template.template_company_name,
    company_address: template.company_address,
    phone_number: template.phone_number,
    fax_number: template.fax_number,
    sections_list: template.sections,
    statement_inputs: template.statement_inputs,
    signature_image_url,
    signed_by: template.signed_by,
    designation: template.designation,
    preview_pdf_url: template.preview_pdf_url,
    alignment: template.alignment || "Left",
    side,
  };
};

export default function ImportTemplatePanel({ onClose, onImported }) {
  const { side } = useTemplateContext();
  const importSide = getOppositeTemplateSide(side);
  const importSideLabel = importSide === TEMPLATE_SIDES.PRODUCT ? "Product" : "Environment";
  const [importTemplates, setImportTemplates] = React.useState([]);
  const { loading: listLoading, run: runListAction, abort: abortListAction } = useTemplateAsyncAction();
  const { loadingKey: importLoadingKey, run: runImportAction, abort: abortImportAction } = useTemplateAsyncAction();

  useEffect(() => {
    runListAction(async (signal) => {
      const response = await getTemplates({ side: importSide }, signal);
      if (response?.cancel || signal?.aborted) {
        return undefined;
      }
      if (!response || !response.success) {
        toast.error(`Could not fetch ${importSideLabel.toLowerCase()} templates.`);
        setImportTemplates([]);
      } else {
        setImportTemplates(response.result || []);
      }
      return response;
    }, { useAbort: true });
    return () => {
      abortListAction();
      abortImportAction();
    };
  }, [importSide, importSideLabel]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleImport = (templateId) => {
    runImportAction(async (signal) => {
      const response = await getTemplates({ template_id: templateId, side: importSide }, signal);
      if (response?.cancel || signal?.aborted) {
        return undefined;
      }
      if (!response || !response.success || !response.result?.[0]) {
        toast.error("Could not fetch template.");
        return undefined;
      }
      if (signal?.aborted) {
        return undefined;
      }
      const saveResponse = await addTemplate(await buildImportPayload(response.result[0], side, signal));
      if (signal?.aborted) {
        return undefined;
      }
      const isDuplicateName = isDuplicateTemplateNameMessage(saveResponse.message);
      if (saveResponse.success && !isDuplicateName) {
        toast.success("Template imported.");
        if (onImported) {
          onImported();
        }
      } else {
        toast.error(saveResponse.message || "Failed to import template.");
      }
      return saveResponse;
    }, { useAbort: true, preventConcurrent: false, key: templateId });
  };

  return (
    <div className="ImportTemplatePanel">
      <div className="ImportTemplatePanel__Header">
        <div style={{ display: "flex" }}>
          <RectangleLine />
          <div>
            <div className="ImportTemplatePanel__Title">Import Template</div>
            <div className="ImportTemplatePanel__Subtitle">{`Reuse ${importSideLabel} Templates`}</div>
          </div>
        </div>
        <button
          type="button"
          className="ImportTemplatePanel__Close"
          aria-label="Close"
          onClick={onClose}
        >
          <CloseOutlined />
        </button>
      </div>
      <StyledScrollbar className="ImportTemplatePanel__ListScroll" disableTracksWidthCompensation>
        <div className="ImportTemplatePanel__List">
          {listLoading && <TemplateSectionLoading variant="inline" />}
          {!listLoading && importTemplates.length === 0 && (
            <div className="ImportTemplatePanel__Empty">{`No ${importSideLabel.toLowerCase()} templates available.`}</div>
          )}
          {!listLoading && importTemplates.map((item) => {
            const itemImporting = importLoadingKey === item.template_id;
            return (
              <button
                type="button"
                key={item.template_id}
                className={`ImportTemplatePanel__Item${itemImporting ? " ImportTemplatePanel__Item--loading" : ""}`}
                disabled={itemImporting}
                onClick={() => handleImport(item.template_id)}
              >
                <span className="ImportTemplatePanel__Accent" style={getTemplateAccentStyle(item)} />
                <span className="ImportTemplatePanel__Name" title={item.name}>{item.name}</span>
                <ImportTemplateIcon className="ImportTemplatePanel__Icon" aria-hidden />
                {itemImporting && (
                  <TemplateSectionLoading variant="overlay" className="ImportTemplatePanel__ItemLoading" />
                )}
              </button>
            );
          })}
        </div>
      </StyledScrollbar>
    </div>
  );
}
